import { useLiveProfile, type DepthRung } from "../hooks/useLiveProfile.ts";

// Live-profile-sourced depth summary for DEX (CLMM) venues. Unlike DepthCard
// (which reads precomputed book_l2_depth_buckets — not populated for DEX), this
// reads the live-profile endpoint, the same source as DepthLadder, and presents
// the AMM's simulated pool-curve depth. No spread is shown: an AMM has a single
// pool price (best_bid == best_ask == mid), so a bid/ask spread is undefined.

const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const VIOLET = "#9b6fe0"; // DEX accent (matches the DEX violet used elsewhere)
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

// the three bps rungs that mirror the CEX ±0.5/1/2% card bands (touch-anchored
// here, so labelled in bps to be honest about the differing reference).
const CARD_BANDS: { bps: number; label: string }[] = [
  { bps: 50, label: "±50bps" },
  { bps: 100, label: "±100bps" },
  { bps: 200, label: "±200bps" },
];

function fmtNotional(n: number | null): string {
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (a >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return n.toFixed(0);
}

// Human pool-price line for the client-facing card: "$0.000368 per BNKR ·
// 5.08M BNKR/WETH" — never scientific notation. Falls back to native-only
// when the quote USD rate is unavailable.
function fmtNativeQty(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (a >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (a >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return n.toPrecision(3);
}
function fmtPoolPrice(
  mid: number | null,
  rate: number | null,
  base: string,
  quote: string,
): string {
  if (mid == null || mid <= 0) return "pool price —";
  const native = `${fmtNativeQty(1 / mid)} ${base}/${quote}`;
  if (rate == null || rate <= 0) return `pool price ${native}`;
  const usd = mid * rate;
  const usdStr = usd >= 0.01 ? `$${usd.toPrecision(5)}` : `$${usd.toFixed(6)}`;
  return `pool price ${usdStr} per ${base} · ${native}`;
}

function rungAt(rungs: DepthRung[], bps: number): DepthRung | undefined {
  return rungs.find((r) => r.bps === bps);
}

export function DexDepthCard({
  venue,
  pair,
  clientId: _clientId,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  const q = useLiveProfile(venue, pair);
  const prof = q.data;
  const quote = pair.split("/")[1] ?? "";
  const notionalUnit = prof?.notional_unit ?? quote;

  const ok = prof?.status === "ok";

  // Headline = ±200bps cumulative, bid + ask (the contractable depth figure,
  // analogous to the CEX card's ±2% bid+ask but touch-anchored bps).
  const bid200 = prof ? rungAt(prof.bid_rungs, 200)?.cumulative_quote ?? null : null;
  const ask200 = prof ? rungAt(prof.ask_rungs, 200)?.cumulative_quote ?? null : null;
  const headline =
    bid200 != null && ask200 != null ? bid200 + ask200 : null;

  const valueColor = ok ? TEXT : MUTED;
  const ageStr =
    prof?.anchor_age_seconds == null ? "—" : `${prof.anchor_age_seconds}s`;

  // Mirrors the soak monitor's stale_after so alerting and UI share ONE
  // definition of stale (26 Aug incident: panels served 50h-old data with
  // only a raw seconds counter in the footnote).
  const STALE_AFTER_SECONDS = 600;
  const staleAge = prof?.anchor_age_seconds ?? null;
  const isStale = ok && staleAge != null && staleAge > STALE_AFTER_SECONDS;
  const staleAgeHuman =
    staleAge == null ? "" :
    staleAge < 5400 ? `${Math.round(staleAge / 60)} minutes` :
    `${Math.round(staleAge / 3600)} hours`;

  return (
    <div
      style={{
        background: CARD_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding: "12px 14px",
        minWidth: 240,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 12, color: MUTED }}>Pool depth</span>
        <span
          style={{
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 5,
            color: ok ? VIOLET : AMBER,
            border: `1px solid ${(ok ? VIOLET : AMBER)}55`,
          }}
          title="Simulated from the pool's concentrated-liquidity curve (clamm_slippage_simulated)."
        >
          {ok ? "simulated" : (prof?.status ?? "…")}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span
          style={{
            fontSize: 24,
            fontWeight: 500,
            color: valueColor,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtNotional(headline)}
        </span>
        <span style={{ fontSize: 12, color: MUTED }}>
          {notionalUnit} ±200bps (bid+ask)
        </span>
      </div>

      {/* per-band cumulative, bid vs ask (touch-anchored bps) */}
      <div style={{ marginTop: 10 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr 1fr",
            gap: "3px 12px",
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
            color: valueColor,
          }}
        >
          <span style={{ color: MUTED, fontSize: 11 }} />
          <span style={{ color: MUTED, fontSize: 11 }}>bid</span>
          <span style={{ color: MUTED, fontSize: 11 }}>ask</span>
          {CARD_BANDS.map((b) => {
            const bc = prof ? rungAt(prof.bid_rungs, b.bps)?.cumulative_quote ?? null : null;
            const ac = prof ? rungAt(prof.ask_rungs, b.bps)?.cumulative_quote ?? null : null;
            return (
              <Band key={b.bps} label={b.label} bidQ={bc} askQ={ac} />
            );
          })}
        </div>
      </div>

      {/* pool price + provenance footnote */}
      <div
        style={{
          fontSize: 11,
          color: MUTED,
          marginTop: 8,
          borderTop: `1px solid ${BORDER}`,
          paddingTop: 7,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmtPoolPrice(
          prof?.mid ?? null,
          (prof as { quote_usd_rate?: number | null } | null)?.quote_usd_rate ?? null,
          pair.split("/")[0] ?? "",
          quote,
        )}
        <br />
        simulated pool-curve depth · anchor {ageStr}
      </div>

      {isStale && (
        <div style={{
          fontSize: 12, color: "#e5484d", marginTop: 6, lineHeight: 1.4,
          fontWeight: 600, border: "1px solid #e5484d", borderRadius: 4,
          padding: "4px 8px",
        }}>
          STALE DATA — last pool snapshot is {staleAgeHuman} old
          (freshness bound 10m). Values shown are historical, not live.
        </div>
      )}
      {prof && prof.status !== "ok" && (
        <div style={{ fontSize: 11, color: AMBER, marginTop: 6, lineHeight: 1.4 }}>
          {prof.status === "no_data"
            ? "No live pool snapshot available."
            : prof.status}
        </div>
      )}
      {q.isError && (
        <div style={{ fontSize: 11, color: AMBER, marginTop: 6 }}>could not load</div>
      )}
    </div>
  );
}

function Band({
  label,
  bidQ,
  askQ,
}: {
  label: string;
  bidQ: number | null;
  askQ: number | null;
}) {
  return (
    <>
      <span style={{ color: MUTED, fontSize: 11 }}>{label}</span>
      <span>{fmtNotional(bidQ)}</span>
      <span>{fmtNotional(askQ)}</span>
    </>
  );
}
